import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import { motion } from 'framer-motion';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply, faTrash, faPaperPlane, faUser, faClock, faEnvelope, faChevronDown, faChevronRight, faFileAlt, faFilePdf, faDownload, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { EmailThread } from '../../models/EmailThreadModel';
import { fetchpipeline, fetchPollingData, getEmailDetails } from '../../services/emailService';
import { fadeIn, PageContainer } from '../../styles/PageStyles';
import { downloadImage } from '../../services/fileUploadService';
import { DocumentItem } from '../document/DocumentItem';
import { FaUser } from 'react-icons/fa';
import { fetchTaskDetails } from '../../services/TaskService';
import CoPilotChat from './CoPilotChat';

const EmailContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
`;

const EmailTaskContainer = styled.div`
  display: flex;
  height: calc(100vh - 126px);
  background: #f5f5f5;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EmailHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e0e0e0;
  background: #ffffff;
`;

const EmailMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #666;
  font-size: 0.9rem;
`;

const EmailThreadsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const SenderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SenderAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eaf4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e88e5;
  font-weight: 700;
  font-size: 18px;
`;

const SubjectStrip = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #333;
  font-weight: 600;
`;

const OriginalMessageCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 18px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  margin-bottom: 18px;
`;

const ReplyRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ReplyBubble = styled.div`
  background: white;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  flex: 1;
`;

const EmailItem = styled.div`
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  background: ${props => props.isReply ? '#f8f9fa' : 'white'};
  border: 1px solid #e0e0e0;
`;

const EmailItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
  }
`;

const EmailTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const EmailTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 1rem;
  font-weight: 600;
`;

const ChevronIcon = styled.div`
  color: #666;
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const EmailContentWrapper = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: ${props => props.expanded ? '1000px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
`;

const EmailSender = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #333;
`;

const EmailTime = styled.div`
  color: #6b7280;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const EmailContent = styled.div`
  color: #555;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const AttachmentsSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const AttachmentsTitle = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const EmailHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content:space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 10px;
  width: 100%;
`;

const EmailHeaderAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  ${prop => prop.inBound ? `
     color: #1e88e5;
     background: #e3f2fd;
    ` : `
    color: #C026D3;
    background: #F5D0FE;
    `}
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const Email = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    border-color: #0066FF;
  }
`;

const AttachmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const AttachmentIcon = styled.div`
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F44336;
  font-size: 1.2rem;
  border: 1px solid #e0e0e0;
`;

const AttachmentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const AttachmentName = styled.div`
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
`;

const AttachmentSize = styled.div`
  font-size: 0.75rem;
  color: #999;
`;

const DownloadButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0066FF;
    color: white;
    border-color: #0066FF;
  }
`;

const ReplySection = styled.div`
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #0066FF;
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.2);
  }
`;

const ReplyActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: #0066FF;
    color: white;
    
    &:hover {
      background: #0052CC;
    }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #ddd;
    
    &:hover {
      background: #f5f5f5;
    }
  `}
`;

const buildPrompt = (taskPayload) => `You are a Claim Intake Review Assistant operating in a Human-in-the-Loop (HITL) workflow.\nYour task is to generate the very first message shown to a human reviewer when the chat opens.\n\nYou are provided structured claim intake data as input as follows:${taskPayload}\n\nYou have access to the following tools:\n- Fetch Policy Details\n- Query Critical Illness Coverages\n\n--------------------\nCLAIM UNDERSTANDING & CLASSIFICATION (MANDATORY)\n--------------------\nBefore generating the response, you must classify the claim using ONLY the following fields:\n- type_of_claim\n- illness_description\n- primary_diagnosis\n- procedure_performed\n- length_of_stay_days\n\nBased on this classification:\n- Determine whether the claim is a potential Critical Illness (CI) claim or a standard Hospitalisation claim.\n- Do NOT assume CI applicability unless the \"Query Critical Illness Coverages\" tool strongly suggests a CI condition.\n\n--------------------\nCRITICAL ILLNESS KNOWLEDGE BASE USAGE RULES\n--------------------\nInvoke \"Query Critical Illness Coverages\" to understand if:\n- The illness or diagnosis may plausibly fall under Critical Illness definitions, OR\n- You need to explicitly rule out CI applicability for the reported condition.\n\nWhen querying the CI Knowledge Base:\n- Use a narrow, confirmatory query\n- Include ONLY:\n  - Illness / Diagnosis\n  - Procedure performed (if any)\n  - Claim type\n  - Policy number\n- Do NOT include billing, doctor, hospital, or medication details.\n\nIf CI coverage is not applicable:\n- Clearly state this in the summary as a validation outcome\n- Route the claim as a Hospitalisation claim\n\n--------------------\nINPUT HANDLING RULES\n--------------------\n1. Extract the policy number from the input claim form.\n2. If a policy number is present, invoke the \"Fetch Policy Details\" tool to retrieve:\n   - Policyholder name\n   - Policy status\n   - Coverage applicability\n3. If any field is missing or null, mark it clearly as:\n   \"Not available / Not extracted yet\"\n4. Do NOT expose tool responses or raw JSON in your message.\n\n--------------------\nOUTPUT OBJECTIVE\n--------------------\nProduce a warm, professional greeting followed by a crisp, structured summary\nthat allows the reviewer to quickly decide whether the claim can be registered.\n\nThe tone should be:\n- Clear\n- Professional\n- Supportive\n- Decision-oriented\n\n--------------------\nOUTPUT STRUCTURE (MANDATORY)\n--------------------\n{\nuser_response: <markdown text response to be sent to the with question if an email regarding outcome should be sent to the customer>,\npipeline_request: <json response to be sent to the claim intake pipeline (only generated if all required details are available and claim can be registered successfully)>\n}\n\n**User Response should be designed in the following manner:**\n1. Friendly greeting and context setting\n2. Sectioned summary using markdown:\n   - Policy Details\n   - Claimant Information\n   - Medical & Incident Details\n   - Claim Type & Coverage Validation\n   - Documents Uploaded\n3. Clearly indicate:\n   - Whether Critical Illness coverage is applicable or not\n   - The reason for that determination (in simple policy-aligned terms)\n4. End with a clear \"Your Action Required\" section listing:\n   - Confirm & Proceed\n   - Request Correction\n   - Reject\n\n--------------------\nSTYLE GUIDELINES\n--------------------\n- Use emojis sparingly for clarity and warmth\n- Be concise but complete\n- Assume the reviewer is a trained claims professional\n- Never make assumptions beyond available data\n- Never mention internal tool names in the final response\n\n--------------------\nFINAL INSTRUCTION\n--------------------\nGenerate the response immediately based on the provided input.\nThis message must stand alone and require no follow-up explanation.`;


const ThreadedEmail = ({ threadId, initialThread = null }) => {
  const [replyText, setReplyText] = useState('');
  const [expandedEmails, setExpandedEmails] = useState({});
  const [taskPaylod, setTaskPayload] = useState(null)
  const [messagesChats, setMessagesChats] = useState([]);
  const[messageLoader, setmessageLoader] = useState(false);
  // Initialize email thread as empty; we'll load details for selected threadId
  const pollingRef = useRef(null);
  const [emailThread, setEmailThread] = useState(() => new EmailThread({}));
  // const promt = `You are a Claim Intake Review Assistant operating in a Human-in-the-Loop (HITL) workflow.\nYour task is to generate the very first message shown to a human reviewer when the chat opens.\n\nYou are provided structured claim intake data as input as follows:${taskPaylod}\n\nYou have access to the following tools:\n- Fetch Policy Details\n- Query Critical Illness Coverages\n\n--------------------\nCLAIM UNDERSTANDING & CLASSIFICATION (MANDATORY)\n--------------------\nBefore generating the response, you must classify the claim using ONLY the following fields:\n- type_of_claim\n- illness_description\n- primary_diagnosis\n- procedure_performed\n- length_of_stay_days\n\nBased on this classification:\n- Determine whether the claim is a potential Critical Illness (CI) claim or a standard Hospitalisation claim.\n- Do NOT assume CI applicability unless the \"Query Critical Illness Coverages\" tool strongly suggests a CI condition.\n\n--------------------\nCRITICAL ILLNESS KNOWLEDGE BASE USAGE RULES\n--------------------\nInvoke \"Query Critical Illness Coverages\" to understand if:\n- The illness or diagnosis may plausibly fall under Critical Illness definitions, OR\n- You need to explicitly rule out CI applicability for the reported condition.\n\nWhen querying the CI Knowledge Base:\n- Use a narrow, confirmatory query\n- Include ONLY:\n  - Illness / Diagnosis\n  - Procedure performed (if any)\n  - Claim type\n  - Policy number\n- Do NOT include billing, doctor, hospital, or medication details.\n\nIf CI coverage is not applicable:\n- Clearly state this in the summary as a validation outcome\n- Route the claim as a Hospitalisation claim\n\n--------------------\nINPUT HANDLING RULES\n--------------------\n1. Extract the policy number from the input claim form.\n2. If a policy number is present, invoke the \"Fetch Policy Details\" tool to retrieve:\n   - Policyholder name\n   - Policy status\n   - Coverage applicability\n3. If any field is missing or null, mark it clearly as:\n   \"Not available / Not extracted yet\"\n4. Do NOT expose tool responses or raw JSON in your message.\n\n--------------------\nOUTPUT OBJECTIVE\n--------------------\nProduce a warm, professional greeting followed by a crisp, structured summary\nthat allows the reviewer to quickly decide whether the claim can be registered.\n\nThe tone should be:\n- Clear\n- Professional\n- Supportive\n- Decision-oriented\n\n--------------------\nOUTPUT STRUCTURE (MANDATORY)\n--------------------\n{\nuser_response: <markdown text response to be sent to the with question if an email regarding outcome should be sent to the customer>,\npipeline_request: <json response to be sent to the claim intake pipeline (only generated if all required details are available and claim can be registered successfully)>\n}\n\n**User Response should be designed in the following manner:**\n1. Friendly greeting and context setting\n2. Sectioned summary using markdown:\n   - Policy Details\n   - Claimant Information\n   - Medical & Incident Details\n   - Claim Type & Coverage Validation\n   - Documents Uploaded\n3. Clearly indicate:\n   - Whether Critical Illness coverage is applicable or not\n   - The reason for that determination (in simple policy-aligned terms)\n4. End with a clear \"Your Action Required\" section listing:\n   - Confirm & Proceed\n   - Request Correction\n   - Reject\n\n--------------------\nSTYLE GUIDELINES\n--------------------\n- Use emojis sparingly for clarity and warmth\n- Be concise but complete\n- Assume the reviewer is a trained claims professional\n- Never make assumptions beyond available data\n- Never mention internal tool names in the final response\n\n--------------------\nFINAL INSTRUCTION\n--------------------\nGenerate the response immediately based on the provided input.\nThis message must stand alone and require no follow-up explanation.`


  console.log("Thread Id", threadId)

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const fetchTaskDetail = async () => {
    const payload = {
      filter: {
        company_cd: "MMS",
        type: "task_service",
        pipeline_id: threadId
      },
      sort: { created_at: "DESC" }
    };

    const resp = await fetchTaskDetails(payload);
    const data = resp?.[0];

    const dataMap =
      data?.workflow_vars?.data_map
        ? JSON.stringify(data.workflow_vars.data_map, null, 2)
        : '{}';

    setTaskPayload(dataMap);

    // fetchpipelineMarkdown(dataMap);
  };

  const fetchpipelineMarkdown = async (taskPayload) => {
    if (!taskPayload) {
      console.error("Task payload missing — aborting pipeline call");
      return;
    }

    const data = {
      payload: {
        markdown: buildPrompt(taskPayload)
      },
      pipeline_id: null,
      document_urls: [],
      async_execution: true
    };

    console.log("Final prompt →", data.payload.markdown);

    let resp = await fetchpipeline(data);
    // resp = { "pipeline_id": "ec0cc1ad-8c2e-478b-9e67-4dd97e80d546", "payload": {} }

    if (resp && resp.pipeline_id) {
      // Do NOT inject an automatic user query into the CoPilot chat here.
      // Previously we added a 'Show me the claims data' user message which caused
      // the CoPilotChat to appear to auto-send on render. Keep the pipeline polling
      // behaviour but avoid adding a user message so the chat remains passive until
      // the user explicitly asks a question.
      setmessageLoader(true);
      startPolling(resp.pipeline_id);
    }
  };


  const startPolling = (pipelineId) => {
    if (!pipelineId) return;

    // prevent duplicate polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(async () => {
      try {
        const filters = {
          pipeline_id: pipelineId
        }
        const resp = await fetchPollingData(filters);

        console.log("Polling response →", resp);

        if (resp[0]?.response?.user_response) {
          setMessagesChats(prev => [
            ...prev,
            {id: Date.now(), message: resp[0]?.response?.user_response }
          ]);
          setmessageLoader(false)
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch (err) {
        console.error("Polling failed", err);
      }
    }, 4000);
  };
          console.log("messages chats", messagesChats)


  const fethPollingData = () => {
    try {

    } catch (error) {
      console.log("Error while fetching the polling data")
    }
  }


  useEffect(() => {
    fetchTaskDetail();

  }, [threadId])
  const convertIntoThreadModel = useCallback((resp) => {
    // resp is expected to be an array of message objects for the thread
    if (Array.isArray(resp) && resp.length) {
      const last = resp[resp.length - 1];
      const threadModel = {
        threadId,
        subject: last.subject || last.thread_subject || '',
        status: last.status || 'IN_PROGRESS',
        createdAt: last.created_at || last.createdAt || '',
        updatedAt: last.updated_at || last.updatedAt || '',
        messages: resp
      };

      setEmailThread(new EmailThread(threadModel));
      console.log('threadModel', threadModel);
      return;
    }

    // If resp is a single object containing thread metadata + messages
    if (resp && resp.messages) {
      setEmailThread(new EmailThread(resp));
      return;
    }

    // Otherwise set an empty thread
    setEmailThread(new EmailThread({}));

  }, [threadId]);

  // Helper to pick message HTML: prefer html_body/htmlBody/html, then fall back to body/body_content/preview
  // If the selected content is plain text, preserve original formatting by converting
  // paragraphs and newlines to HTML (<p> and <br/>). If it already contains HTML tags,
  // sanitize and return as-is.
  const getMessageHtml = (msg) => {
    if (!msg) return '';
    const raw = msg.html_body || msg.htmlBody || msg.html || msg.body || msg.body_content || msg.preview || '';

    const asString = String(raw || '');

    // Quick heuristic to decide if content already contains HTML tags
    const containsHtml = /<[^>]+>/.test(asString);

    if (containsHtml) {
      return DOMPurify.sanitize(asString);
    }

    // Escape HTML-sensitive characters for plain text
    const escapeHtml = (s) =>
      s.replace(/&/g, '&amp;')
       .replace(/</g, '&lt;')
       .replace(/>/g, '&gt;')
       .replace(/\"/g, '&quot;')
       .replace(/'/g, '&#039;');

    const escaped = escapeHtml(asString);

    // Convert double-newlines (paragraphs) into <p> blocks, single newlines into <br/>
    const paragraphs = escaped.split(/\n{2,}/).map(p => p.replace(/\n/g, '<br/>'));

    // Wrap paragraphs with spacing so the rendered email looks like the original
    return paragraphs.map(p => `<p style="margin:0 0 1rem 0">${p}</p>`).join('');
  };

  const location = useLocation();

  useEffect(() => {
    // Prefer initialThread prop (passed via EmailTask navigation state), then location.state
    const threadFromNav = initialThread || (location && location.state && location.state.emailThread);

    if (threadFromNav) {
      // If the caller passed a thread object, set it directly
      setEmailThread(new EmailThread(threadFromNav));
      return;
    }

    // If no initial data was provided, clear the current thread so UI shows empty state
    setEmailThread(new EmailThread({}));
  }, [threadId, initialThread, location]);


  const toggleEmailExpansion = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  /**
  * Check if message is outbound (from customer)
  */
  const isOutbound = (type) => {
    return type === 'OUTBOUND';
  }

  /**
   * Check if message is inbound (from insurance company)
   */
  const isInbound = (type) => {
    // return type === 'INBOUND';
    return type === 'INBOUND';

  }

  const handleDownloadAttachment = async (attachment) => {

    console.log('Downloading attachment:', attachment);
    // TODO: Implement actual download logic
    if (attachment) {
      const resp = await downloadImage(attachment.url, attachment.fileName);
    }
  };


  const getFormattedUpdatedAt = (updatedAt) => {
    console.log("inside format date", updatedAt)
    const date = new Date(updatedAt);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const getFormattedSize = (size) => {
    const bytes = size;
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  const getFormattedTime = (createdAt) => {
    const date = new Date(createdAt);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const getSortedMessages = useCallback((emailThreadMessages) => {
    // Normalize sort key: prefer sequence, then version, then sent/created timestamp
    const safeValue = (m) => {
      if (m == null) return 0;
      if (typeof m.sequence === 'number') return m.sequence;
      if (typeof m.version === 'number') return m.version;
      const dt = m.sentAt || m.created_at || m.createdAt || m.updated_at || m.updatedAt;
      const t = dt ? new Date(dt).getTime() : 0;
      return t;
    };

    // Return a new sorted array (don't mutate original)
    return (emailThreadMessages.messages || []).slice().sort((a, b) => safeValue(a) - safeValue(b));
  }, [threadId])

  const handleSendReply = () => {
    if (replyText.trim()) {
      console.log('Sending reply:', replyText);
      setReplyText('');
    }
  };


  // If no thread data available show a helpful message
  if (!emailThread || !emailThread.messages || emailThread.messages.length === 0) {
    return (
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <EmailTaskContainer>
          <MainContent>
            <EmailHeader>
              <h2 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.2rem', fontWeight: '600' }}>
                {emailThread.subject || 'No email selected'}
              </h2>
              <EmailMeta>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: '#1e88e5' }} />
                  <span>Thread ID: {emailThread.threadId || threadId || '—'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faClock} />
                  <span>Last updated: {emailThread.updatedAt ? getFormattedUpdatedAt(emailThread.updatedAt) : '—'}</span>
                </div>
              </EmailMeta>
            </EmailHeader>
            <EmailThreadsContainer>
              <div style={{ padding: 24, color: '#666' }}>
                Select an email from the list to view its details.
              </div>
            </EmailThreadsContainer>
          </MainContent>
            <CoPilotChat
              messages={messagesChats}
              messageLoader={messageLoader}
              sessionId={emailThread.threadId || threadId}
              initialCustomerName={(emailThread.messages && emailThread.messages[0]) ? (emailThread.messages[0].customerName || '') : ''}
            />
        </EmailTaskContainer>
      </motion.div>
    );
  }

  return (

    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <EmailTaskContainer>
        <MainContent>
          <EmailHeader>
            <SenderHeader>
              <SenderAvatar>
                {(() => {
                  const first = (emailThread.messages && emailThread.messages[0]);
                  const name = first?.senderName || first?.customerName || first?.from || '';
                  return (name && name[0]) ? name[0].toUpperCase() : 'U';
                })()}
              </SenderAvatar>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{(emailThread.messages && emailThread.messages[0]) ? (emailThread.messages[0].senderName || emailThread.messages[0].customerName || emailThread.messages[0].from) : '—'}</div>
                <div style={{ color: '#6b7280', fontSize: 13 }}>{(emailThread.messages && emailThread.messages[0]) ? (emailThread.messages[0].from || '') : ''}</div>
              </div>
            </SenderHeader>

            <SubjectStrip>
              <FontAwesomeIcon icon={faEnvelope} style={{ color: '#1e88e5' }} />
              <div style={{ fontWeight: 600 }}>{emailThread.subject}</div>
            </SubjectStrip>
          </EmailHeader>

          <EmailThreadsContainer>
            {(() => {
              const msgs = getSortedMessages(emailThread) || [];
              if (msgs.length === 0) return null;

              const [firstMsg, ...rest] = msgs;
              const firstBody = getMessageHtml(firstMsg);

              return (
                <>
                  <OriginalMessageCard>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <SenderAvatar style={{ width: 48, height: 48, fontSize: 16 }}>{(firstMsg.senderName || firstMsg.customerName || firstMsg.from || '').charAt(0).toUpperCase() || 'U'}</SenderAvatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{firstMsg.senderName || firstMsg.customerName || firstMsg.from}</div>
                        <div style={{ color: '#6b7280', fontSize: 13 }}>{firstMsg.from}</div>
                        <div style={{ marginTop: 12 }} dangerouslySetInnerHTML={{ __html: firstBody }} />
                      </div>
                    </div>
                    {Array.isArray(firstMsg.attachments) && firstMsg.attachments.length > 0 && (
                      <AttachmentsSection style={{ marginTop: 12 }}>
                        <AttachmentsTitle>
                          <FontAwesomeIcon icon={faPaperclip} />
                          Attachments ({firstMsg.attachments.length})
                        </AttachmentsTitle>
                        <AttachmentsList>
                          {firstMsg.attachments.map((attachment, i) => (
                            <DocumentItem key={i} document={attachment} onDownload={() => handleDownloadAttachment(attachment)} onDocumentClick={() => handleDownloadAttachment(attachment)} hasLink={true} />
                          ))}
                        </AttachmentsList>
                      </AttachmentsSection>
                    )}
                  </OriginalMessageCard>

                  {rest.map((message) => {
                    const type = message.type;
                    const version = message.version ?? message.sequence ?? Date.now();
                    const from = message.from || message.from_email || '';
                    const senderName = message.sender_name || message.senderName || message.customerName || '';
                    const createAt = message.created_at || message.sentAt || message.createdAt;
                    const body = getMessageHtml(message);

                    return (
                      <ReplyRow key={version}>
                        <SenderAvatar style={{ width: 36, height: 36, fontSize: 12 }}>{(senderName || from || 'U').charAt(0).toUpperCase()}</SenderAvatar>
                        <ReplyBubble>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>{senderName || from}</div>
                          <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>{getFormattedTime(createAt)}</div>
                          <div dangerouslySetInnerHTML={{ __html: body }} />

                          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                            <AttachmentsSection style={{ marginTop: 12 }}>
                              <AttachmentsTitle>
                                <FontAwesomeIcon icon={faPaperclip} />
                                Attachments ({message.attachments.length})
                              </AttachmentsTitle>
                              <AttachmentsList>
                                {message.attachments.map((attachment, idx) => (
                                  <DocumentItem key={idx} document={attachment} onDownload={() => handleDownloadAttachment(attachment)} onDocumentClick={() => handleDownloadAttachment(attachment)} hasLink={true} />
                                ))}
                              </AttachmentsList>
                            </AttachmentsSection>
                          )}
                        </ReplyBubble>
                      </ReplyRow>
                    );
                  })}
                </>
              );
            })()}
          </EmailThreadsContainer>
        </MainContent>
            <CoPilotChat
              messages={messagesChats}
              messageLoader={messageLoader}
              sessionId={emailThread.threadId || threadId}
              initialCustomerName={(emailThread.messages && emailThread.messages[0]) ? (emailThread.messages[0].customerName || '') : ''}
            />

      </EmailTaskContainer>
    </motion.div>


  );
};

export default ThreadedEmail;
